import type {CaptureDefaultValues, ValidationRegistrar} from 'modules/VendorRegister/types';

import {yupResolver} from '@hookform/resolvers/yup';
import {toast} from '@Toast';
import FormInput from 'modules/VendorRegister/components/common/FormInput/FormInput';
import {useCallback, useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch} from 'store';
import {sendFaceRecognitionInfo} from 'store/slices/vendor-registration/thunk';
import {p2e} from 'utils/Helper';

import CaptureCard from './components/CaptureCard/CaptureCard';
import ImageCaptureModal from './components/ImageCaptureModal/ImageCaptureModal';
import VideoRecordModal from './components/VideoRecordModal/VideoRecordModal';
import {FACE_RECOGNITION_SCHEMA} from './constants';

type FaceRecognitionForm = {
  nationalCardSerial: string;
};

type FaceRecognitionVerificationProps = {
  registerValidation: ValidationRegistrar;
};

const FaceRecognitionVerification: React.FC<FaceRecognitionVerificationProps> = ({
  registerValidation
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [userImage, setUserImage] = useState<CaptureDefaultValues>({id: null, file: null});
  const [userVideo, setUserVideo] = useState<CaptureDefaultValues>({id: null, file: null});
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const {control, handleSubmit} = useForm<FaceRecognitionForm>({
    resolver: yupResolver(FACE_RECOGNITION_SCHEMA),
    mode: 'onChange',
    defaultValues: {
      nationalCardSerial: ''
    }
  });

  const submitFaceRecognition = useCallback(
    async (data: FaceRecognitionForm) => {
      if (!userVideo.id) {
        toast(' مرحله ضبط ویدیو را انجام دهید');
        return false;
      }

      if (!userImage.id) {
        toast('مرحله برداشت تصویر را انجام دهید');
        return false;
      }

      try {
        const response = await dispatch(
          sendFaceRecognitionInfo({
            personVideo: userVideo.id,
            personImage: userImage.id,
            nationalCardSerial: p2e(data.nationalCardSerial).toUpperCase()
          })
        ).unwrap();

        if (response.isSuccess) {
          return true;
        }

        toast(response.message || 'خطا');
      } catch (error) {
        if (
          typeof error === 'object' &&
          error !== null &&
          'data' in error &&
          typeof error.data === 'object' &&
          error.data !== null &&
          'status' in error.data &&
          error.data.status === 403
        ) {
          navigate('/vendor-register-overview');
        }
      }

      return false;
    },
    [dispatch, navigate, userImage.id, userVideo.id]
  );

  const validateStep = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      void handleSubmit(
        async (data) => {
          resolve(await submitFaceRecognition(data));
        },
        () => resolve(false)
      )();
    });
  }, [handleSubmit, submitFaceRecognition]);

  useEffect(() => {
    registerValidation(validateStep);
  }, [registerValidation, validateStep]);

  const requestCamera = async (openModal: React.Dispatch<React.SetStateAction<boolean>>) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {facingMode: 'user'},
        audio: false
      });
      stream.getTracks().forEach((track) => track.stop());
      openModal(true);
    } catch {
      toast('برای استفاده از دوربین، دسترسی به آن را مجاز کنید');
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <CaptureCard
        title="ضبط ویدیو"
        emptyText="حداکثر ۲۰ ثانیه"
        buttonText="ضبط ویدیو"
        file={userVideo.file}
        type="video"
        onCapture={() => void requestCamera(setShowVideoModal)}
      />
      <CaptureCard
        title="برداشت تصویر"
        emptyText="نمایش دوربین"
        buttonText="برداشت تصویر"
        file={userImage.file}
        type="image"
        onCapture={() => void requestCamera(setShowImageModal)}
      />
      <FormInput
        name="nationalCardSerial"
        control={control}
        placeholder="مثال: oMC8745340"
        label="سریال پشت کارت ملی"
        required
      />
      <VideoRecordModal
        defaultValues={userVideo}
        show={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        onRecordingComplete={(id, file) => setUserVideo({id, file})}
      />
      <ImageCaptureModal
        defaultValues={userImage}
        show={showImageModal}
        onClose={() => setShowImageModal(false)}
        onCaptureComplete={(id, file) => setUserImage({id, file})}
      />
    </div>
  );
};

export default FaceRecognitionVerification;
